/**
 * Refresh GenerateBlocks Pro carousels when they become visible inside tabs.
 *
 * GB tabs hide inactive panels with `display: none`. Swiper initializes at
 * width 0 inside those panels and locks; it may unlock on its own when the
 * panel shows, but GB leaves prev/next with `.is-disabled` (click handler
 * no-ops) until `updateNavigationButtons()` runs (SITES-1005).
 */

const TAB_ITEM_SELECTOR = '.gb-tabs__item';
const CAROUSEL_SELECTOR = '.gb-carousel';
const OPEN_CLASS = 'gb-tabs__item-open';
const TRANSITION_REFRESH_MS = 120;

/**
 * @typedef {object} GbCarouselInstance
 * @property {{ destroyed?: boolean, update?: () => void, on?: (event: string, handler: () => void) => void } | undefined} [swiper]
 * @property {(() => void) | undefined} [updateNavigationButtons]
 */

/**
 * @param {Element} element
 */
function syncNavigationButtons(element) {
  const carousel = /** @type {HTMLElement & { gbCarousel?: GbCarouselInstance }} */ (
    element
  );
  const gbCarousel = carousel.gbCarousel;
  const swiper = gbCarousel?.swiper;
  if (!swiper || swiper.destroyed) {
    return;
  }
  if (typeof gbCarousel.updateNavigationButtons !== 'function') {
    return;
  }

  // GB defers button state to rAF and coalesces via navigationUpdateFrame.
  // Call now and again next frame so a stale locked-state pass cannot win.
  gbCarousel.updateNavigationButtons();
  requestAnimationFrame(() => {
    if (!swiper.destroyed) {
      gbCarousel.updateNavigationButtons();
    }
  });
}

/**
 * @param {Element} element
 */
function refreshCarousel(element) {
  const carousel = /** @type {HTMLElement & { gbCarousel?: GbCarouselInstance }} */ (
    element
  );
  const swiper = carousel.gbCarousel?.swiper;
  if (!swiper || swiper.destroyed || typeof swiper.update !== 'function') {
    return;
  }

  swiper.update();
  syncNavigationButtons(carousel);
}

/**
 * @param {Element | null | undefined} root
 */
function refreshCarouselsIn(root) {
  if (!(root instanceof Element)) {
    return;
  }

  if (root.matches(CAROUSEL_SELECTOR)) {
    refreshCarousel(root);
    return;
  }

  root.querySelectorAll(CAROUSEL_SELECTOR).forEach(refreshCarousel);
}

/**
 * @param {Element} root
 */
function scheduleRefresh(root) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      refreshCarouselsIn(root);
      window.setTimeout(() => refreshCarouselsIn(root), TRANSITION_REFRESH_MS);
    });
  });
}

/**
 * @param {string | null} className
 */
function classListHasOpen(className) {
  if (!className) {
    return false;
  }
  return className.split(/\s+/).includes(OPEN_CLASS);
}

/**
 * Primary path: carousels that were `display:none` become visible when their
 * tab opens. Catches Swiper unlocking on its own while GB nav stays disabled.
 *
 * @param {Document} rootDocument
 */
function observeCarouselVisibility(rootDocument) {
  if (typeof IntersectionObserver === 'undefined') {
    return;
  }

  /** @type {WeakSet<Element>} */
  const pending = new WeakSet();

  const visibilityObserver = new IntersectionObserver(
    entries => {
      for (const entry of entries) {
        if (!entry.isIntersecting || entry.intersectionRatio <= 0) {
          continue;
        }
        if (!(entry.target instanceof Element)) {
          continue;
        }
        // Only refresh carousels that live in a tab panel (SITES-1005 scope).
        const panel = entry.target.closest(TAB_ITEM_SELECTOR);
        if (!panel) {
          continue;
        }
        if (pending.has(entry.target)) {
          continue;
        }
        pending.add(entry.target);
        scheduleRefresh(panel);
        window.setTimeout(() => pending.delete(entry.target), TRANSITION_REFRESH_MS + 50);
      }
    },
    { threshold: 0.01 },
  );

  /**
   * @param {Element} carousel
   */
  const observeCarousel = carousel => {
    if (!carousel.closest(TAB_ITEM_SELECTOR)) {
      return;
    }
    visibilityObserver.observe(carousel);
  };

  rootDocument.querySelectorAll(CAROUSEL_SELECTOR).forEach(observeCarousel);

  new MutationObserver(mutations => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) {
          continue;
        }

        const carousels = node.matches(CAROUSEL_SELECTOR)
          ? [node]
          : [...node.querySelectorAll(CAROUSEL_SELECTOR)];

        carousels.forEach(observeCarousel);
      }
    }
  }).observe(rootDocument.documentElement, {
    childList: true,
    subtree: true,
  });
}

/**
 * Backup path: GB toggles `.gb-tabs__item-open` on the panel.
 *
 * @param {Document} rootDocument
 */
function observeTabPanelOpen(rootDocument) {
  if (typeof MutationObserver === 'undefined') {
    return;
  }

  const panelObserver = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      if (mutation.type !== 'attributes' || mutation.attributeName !== 'class') {
        continue;
      }

      const target = mutation.target;
      if (!(target instanceof Element) || !target.matches(TAB_ITEM_SELECTOR)) {
        continue;
      }

      if (!target.classList.contains(OPEN_CLASS)) {
        continue;
      }

      if (classListHasOpen(mutation.oldValue)) {
        continue;
      }

      scheduleRefresh(target);
    }
  });

  /**
   * @param {Element} panel
   */
  const observeTabPanel = panel => {
    panelObserver.observe(panel, {
      attributes: true,
      attributeFilter: ['class'],
      attributeOldValue: true,
    });
  };

  rootDocument.querySelectorAll(TAB_ITEM_SELECTOR).forEach(observeTabPanel);

  new MutationObserver(mutations => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) {
          continue;
        }

        const panels = node.matches(TAB_ITEM_SELECTOR)
          ? [node]
          : [...node.querySelectorAll(TAB_ITEM_SELECTOR)];

        panels.forEach(observeTabPanel);
      }
    }
  }).observe(rootDocument.documentElement, {
    childList: true,
    subtree: true,
  });
}

/**
 * When Swiper unlocks after measuring real width, sync GB's disabled classes
 * without calling `swiper.update()` again (avoids resize loops).
 *
 * @param {Document} rootDocument
 */
function observeSwiperUnlock(rootDocument) {
  /**
   * @param {Element} element
   */
  const bindUnlock = element => {
    const carousel = /** @type {HTMLElement & { gbCarousel?: GbCarouselInstance, __wwTabbedCarouselUnlockBound?: boolean }} */ (
      element
    );
    if (carousel.__wwTabbedCarouselUnlockBound) {
      return;
    }
    if (!carousel.closest(TAB_ITEM_SELECTOR)) {
      return;
    }

    const swiper = carousel.gbCarousel?.swiper;
    if (!swiper || swiper.destroyed || typeof swiper.on !== 'function') {
      return;
    }

    carousel.__wwTabbedCarouselUnlockBound = true;
    swiper.on('unlock', () => syncNavigationButtons(carousel));
  };

  const bindAll = () => {
    rootDocument.querySelectorAll(CAROUSEL_SELECTOR).forEach(bindUnlock);
  };

  bindAll();
  // Carousels may finish init after this module runs.
  window.setTimeout(bindAll, 0);
  window.setTimeout(bindAll, 250);
  window.setTimeout(bindAll, 1000);

  new MutationObserver(mutations => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) {
          continue;
        }

        const carousels = node.matches(CAROUSEL_SELECTOR)
          ? [node]
          : [...node.querySelectorAll(CAROUSEL_SELECTOR)];

        carousels.forEach(bindUnlock);
      }
    }
  }).observe(rootDocument.documentElement, {
    childList: true,
    subtree: true,
  });
}

/**
 * @param {Document} [rootDocument]
 * @param {Window} [rootWindow]
 */
export function initTabbedCarouselRefresh(
  rootDocument = document,
  rootWindow = window,
) {
  if (typeof rootDocument === 'undefined' || typeof rootWindow === 'undefined') {
    return;
  }

  observeCarouselVisibility(rootDocument);
  observeTabPanelOpen(rootDocument);
  observeSwiperUnlock(rootDocument);
}
